import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format, parseISO, isValid } from 'date-fns';
import { it as itLocale, enUS } from 'date-fns/locale';
import { Pin, Pencil, Trash2, ExternalLink, Loader2, Clock } from 'lucide-react';
import { Carousel, CarouselContent, CarouselItem } from '../ui/carousel';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import { Button } from '../ui/button';
import { useToast } from '../ui/use-toast';
import { announcementsService } from '../../services/announcements';
import type { Announcement } from '../../types/announcement';
import AnnouncementFormDialog from './AnnouncementFormDialog';

interface AnnouncementsCarouselProps {
  gymSlug: string;
  isStaffMember: boolean;
  /** Bump this from the parent to force a refetch (e.g. after creating one). */
  reloadToken?: number;
}

export default function AnnouncementsCarousel({
  gymSlug,
  isStaffMember,
  reloadToken = 0,
}: AnnouncementsCarouselProps) {
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Announcement | null>(null);
  const [editing, setEditing] = useState<Announcement | null>(null);
  const [deleting, setDeleting] = useState<Announcement | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    announcementsService
      .list(gymSlug)
      .then(data => { if (active) setAnnouncements(data); })
      .catch(() => { if (active) setAnnouncements([]); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [gymSlug, reloadToken]);

  const handleSaved = (updated: Announcement) => {
    setAnnouncements(prev => prev.map(a => (a.id === updated.id ? updated : a)));
    setEditing(null);
    setSelected(null);
  };

  const handleDelete = async () => {
    if (!deleting) return;
    setDeleteLoading(true);
    try {
      await announcementsService.remove(gymSlug, deleting.id);
      setAnnouncements(prev => prev.filter(a => a.id !== deleting.id));
      toast({ title: t('announcement.deleteSuccess') });
      setDeleting(null);
      setSelected(null);
    } catch {
      toast({ title: t('errors.unexpectedError'), variant: 'destructive' });
    } finally {
      setDeleteLoading(false);
    }
  };

  // Small "when is this shown" label: a start–end range, or a single bound.
  const formatWindow = (announcement: Announcement): string | null => {
    const locale = i18n.language.startsWith('it') ? itLocale : enUS;
    const fmt = (iso: string) => {
      const date = parseISO(iso);
      return isValid(date) ? format(date, 'd MMM, HH:mm', { locale }) : null;
    };
    const start = announcement.starts_at ? fmt(announcement.starts_at) : null;
    const end = announcement.ends_at ? fmt(announcement.ends_at) : null;
    if (start && end) return `${start} – ${end}`;
    if (end) return t('announcement.until', { date: end });
    if (start) return t('announcement.from', { date: start });
    return null;
  };

  if (loading || announcements.length === 0) return null;

  return (
    <>
      <Carousel
        opts={{ align: 'start', containScroll: 'trimSnaps' }}
        className="w-full"
      >
        <CarouselContent>
          {announcements.map(announcement => (
            <CarouselItem key={announcement.id} className="basis-4/5 sm:basis-2/5">
              <div className="group relative flex h-16 items-center gap-3 overflow-hidden rounded-md border bg-card px-3 transition-colors hover:bg-muted/50">
                <button
                  type="button"
                  onClick={() => setSelected(announcement)}
                  className="flex h-full flex-1 items-center gap-3 text-left"
                >
                  {announcement.image_thumb_url ? (
                    <img
                      src={announcement.image_thumb_url}
                      alt=""
                      className="h-10 w-10 shrink-0 rounded object-cover"
                    />
                  ) : null}
                  <span className="flex min-w-0 flex-col">
                    <span className="flex min-w-0 items-center gap-1.5">
                      {announcement.pinned && <Pin className="h-3.5 w-3.5 shrink-0 text-primary" />}
                      <span className="truncate text-sm font-medium">{announcement.title}</span>
                    </span>
                    {announcement.description && (
                      <span className="truncate text-xs text-muted-foreground">
                        {announcement.description}
                      </span>
                    )}
                    {formatWindow(announcement) && (
                      <span className="flex min-w-0 items-center gap-1 text-[11px] text-muted-foreground">
                        <Clock className="h-3 w-3 shrink-0" />
                        <span className="truncate">{formatWindow(announcement)}</span>
                      </span>
                    )}
                  </span>
                </button>

                {isStaffMember && (
                  <div className="flex shrink-0 items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => setEditing(announcement)}
                      aria-label={t('common.edit')}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-destructive"
                      onClick={() => setDeleting(announcement)}
                      aria-label={t('common.delete')}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                )}
              </div>
            </CarouselItem>
          ))}
        </CarouselContent>
      </Carousel>

      {/* Detail dialog */}
      <Dialog open={!!selected} onOpenChange={open => !open && setSelected(null)}>
        <DialogContent className="max-w-md">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="pr-6">{selected.title}</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                {selected.image_url && (
                  <img src={selected.image_url} alt="" className="max-h-60 w-full rounded-md object-cover" />
                )}
                {selected.description && (
                  <p className="whitespace-pre-wrap text-sm text-muted-foreground">{selected.description}</p>
                )}
                {formatWindow(selected) && (
                  <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Clock className="h-3.5 w-3.5 shrink-0" />
                    {formatWindow(selected)}
                  </p>
                )}
              </div>
              <DialogFooter className="flex-col gap-2 sm:flex-col">
                {selected.link_url && (
                  <a href={selected.link_url} target="_blank" rel="noopener noreferrer" className="w-full">
                    <Button className="w-full gap-2">
                      <ExternalLink className="h-4 w-4" />
                      {selected.link_text || t('announcement.openLink')}
                    </Button>
                  </a>
                )}
                {isStaffMember && (
                  <div className="flex w-full gap-2">
                    <Button variant="outline" className="flex-1 gap-2" onClick={() => setEditing(selected)}>
                      <Pencil className="h-4 w-4" />{t('common.edit')}
                    </Button>
                    <Button variant="outline" className="flex-1 gap-2 text-destructive" onClick={() => setDeleting(selected)}>
                      <Trash2 className="h-4 w-4" />{t('common.delete')}
                    </Button>
                  </div>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <AnnouncementFormDialog
        open={!!editing}
        onOpenChange={open => !open && setEditing(null)}
        gymSlug={gymSlug}
        announcement={editing}
        onSaved={handleSaved}
      />

      {/* Delete confirmation */}
      <AlertDialog open={!!deleting} onOpenChange={open => !open && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('announcement.deleteConfirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription>{t('announcement.deleteConfirmBody')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteLoading}>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); handleDelete(); }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
